import {
    collection,
    query,
    where,
    orderBy,
    getDocs,
    limit,
    serverTimestamp,
    writeBatch,
    doc,
    setDoc,
    getDoc,
    deleteDoc,
    onSnapshot,
    Unsubscribe
} from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import { DMConversation, Message } from '../types/directMessages';
import { getUserPublicData } from './userService';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';

class DirectMessagingService {

    // Fetch conversations for a user
    async fetchConversations(userId: string): Promise<DMConversation[]> {
        try {
            // Query conversations where user is a participant
            const conversationsRef = collection(db, 'directMessages');
            const q = query(
                conversationsRef,
                where('participantIds', 'array-contains', userId),
                orderBy('updatedAt', 'desc')
            );

            const querySnapshot = await getDocs(q);

            // Map through conversations and fetch last message and participants for each
            const conversationsWithMessages = await Promise.all(
                querySnapshot.docs.map(async (conversationDoc) => {
                    const conversationData = {
                        id: conversationDoc.id,
                        ...conversationDoc.data()
                    } as DMConversation;

                    // Fetch the last message from the messages subcollection
                    const messagesRef = collection(db, `directMessages/${conversationDoc.id}/messages`);
                    const messagesQuery = query(
                        messagesRef,
                        orderBy('timestamp', 'desc'),
                        limit(1)
                    );

                    const messagesSnapshot = await getDocs(messagesQuery);

                    if (!messagesSnapshot.empty) {
                        const lastMessageDoc = messagesSnapshot.docs[0];
                        conversationData.lastMessage = {
                            id: lastMessageDoc.id,
                            ...lastMessageDoc.data()
                        } as Message;
                    }

                    // Fetch participant details
                    if (conversationData.participantIds && conversationData.participantIds.length > 0) {
                        const participants = await Promise.all(
                            conversationData.participantIds.map(async (participantId) => {
                                const userPublicData = await getUserPublicData(participantId);
                                return {
                                    userId: participantId,
                                    name: userPublicData?.username
                                        ? userPublicData.username.startsWith("@")
                                            ? userPublicData.username
                                            : `@${userPublicData.username}`
                                        : undefined,
                                    imageUrl: userPublicData?.imageUrl,
                                    imageUrlTiny: userPublicData?.imageUrlTiny
                                };
                            })
                        );
                        conversationData.participants = participants;
                    }

                    return conversationData;
                })
            );

            return conversationsWithMessages;
        } catch (error) {
            console.error('Error fetching conversations:', error);
            throw error;
        }
    }

    async fetchMessages(conversationId: string, limitCount: number = 1000): Promise<Message[]> {
        try {
            const messagesRef = collection(db, `directMessages/${conversationId}/messages`);
            const q = query(
                messagesRef,
                orderBy('timestamp', 'desc'),
                limit(limitCount)
            );

            const querySnapshot = await getDocs(q);

            const messages: Message[] = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data()
            } as Message));

            // Return messages in chronological order
            return messages.reverse();
        } catch (error) {
            console.error('Error fetching messages:', error);
            throw error;
        }
    }

    // Real-time listener for messages
    subscribeToMessages(
        conversationId: string,
        onMessagesUpdate: (messages: Message[]) => void,
        limitCount: number = 1000
    ): Unsubscribe {
        const messagesRef = collection(db, `directMessages/${conversationId}/messages`);
        const q = query(
            messagesRef,
            orderBy('timestamp', 'asc'),
            limit(limitCount)
        );

        return onSnapshot(q, (snapshot) => {
            const messages: Message[] = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data()
            } as Message));

            onMessagesUpdate(messages);
        }, (error) => {
            console.error('Error listening to messages:', error);
        });
    }

    // Real-time listener for conversations
    subscribeToConversations(
        userId: string,
        onConversationsUpdate: (conversations: DMConversation[]) => void
    ): Unsubscribe {
        const conversationsRef = collection(db, 'directMessages');
        const q = query(
            conversationsRef,
            where('participantIds', 'array-contains', userId),
            orderBy('updatedAt', 'desc')
        );

        return onSnapshot(q, async (snapshot) => {
            // Map through conversations and fetch last message and participants for each
            const conversationsWithMessages = await Promise.all(
                snapshot.docs.map(async (conversationDoc) => {
                    const conversationData = {
                        id: conversationDoc.id,
                        ...conversationDoc.data()
                    } as DMConversation;

                    // Fetch the last message from the messages subcollection
                    const messagesRef = collection(db, `directMessages/${conversationDoc.id}/messages`);
                    const messagesQuery = query(
                        messagesRef,
                        orderBy('timestamp', 'desc'),
                        limit(1)
                    );

                    const messagesSnapshot = await getDocs(messagesQuery);

                    if (!messagesSnapshot.empty) {
                        const lastMessageDoc = messagesSnapshot.docs[0];
                        conversationData.lastMessage = {
                            id: lastMessageDoc.id,
                            ...lastMessageDoc.data()
                        } as Message;
                    }

                    // Fetch participant details
                    if (conversationData.participantIds && conversationData.participantIds.length > 0) {
                        const participants = await Promise.all(
                            conversationData.participantIds.map(async (participantId) => {
                                const userPublicData = await getUserPublicData(participantId);
                                return {
                                    userId: participantId,
                                    name: userPublicData?.username
                                        ? userPublicData.username.startsWith("@")
                                            ? userPublicData.username
                                            : `@${userPublicData.username}`
                                        : undefined,
                                    imageUrl: userPublicData?.imageUrl,
                                    imageUrlTiny: userPublicData?.imageUrlTiny
                                };
                            })
                        );
                        conversationData.participants = participants;
                    }

                    return conversationData;
                })
            );

            onConversationsUpdate(conversationsWithMessages);
        }, (error) => {
            console.error('Error listening to conversations:', error);
        });
    }

    /* Generate a message id */
    generateConversationId(): string {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const idLength = 22;

        let conversation_id = 'conv_';

        for (let i = 0; i < idLength; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        conversation_id += characters.charAt(randomIndex);
        }

        return conversation_id;
    }

        /* Generate a message id */
    generateMessageId(): string {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const idLength = 22;

        let message_id = 'dm_';

        for (let i = 0; i < idLength; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        message_id += characters.charAt(randomIndex);
        }

        return message_id;
    }

    async createConversation(conversationData: Partial<DMConversation>, message: Message): Promise<DMConversation> {
        try {
            // Generate IDs
            const conversationId = this.generateConversationId();
            const messageId = this.generateMessageId();

            // Create conversation object
            const newConversation: DMConversation = {
                id: conversationId,
                participantIds: conversationData.participantIds || [],
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            // Create message object
            const newMessage: Message = {
                ...message,
                id: messageId,
                timestamp: serverTimestamp(),
            };

            // Batch write to set both conversation and message atomically
            const batch = writeBatch(db);

            // Set conversation document
            const conversationRef = doc(db, 'directMessages', conversationId);
            batch.set(conversationRef, newConversation);

            // Set initial message document
            const messageRef = doc(db, `directMessages/${conversationId}/messages`, messageId);
            batch.set(messageRef, newMessage);

            // Commit the batch
            await batch.commit();

            return { ...newConversation, lastMessage: newMessage, messages: [newMessage] };
        } catch (error) {
            console.error('Error creating conversation:', error);
            throw error;
        }
    }

    async sendMessage(conversationId: string, message: Message): Promise<void> {
        try {
            // Generate message ID
            const messageId = this.generateMessageId();

            // Create message object
            const newMessage: Message = {
                ...message,
                id: messageId,
                timestamp: serverTimestamp(),
            };
            
            // References
            const messageRef = doc(db, `directMessages/${conversationId}/messages`, messageId);
            const conversationRef = doc(db, 'directMessages', conversationId);

            // Batch write to add message and update conversation's updatedAt
            const batch = writeBatch(db);

            // Set new message
            batch.set(messageRef, newMessage);

            // Update conversation's updatedAt
            batch.update(conversationRef, {
                updatedAt: serverTimestamp(),
            });

            // Commit the batch
            await batch.commit();
        } catch (error) {
            console.error('Error sending message:', error);
            throw error;
        }
    }

    async deleteMessage(conversationId: string, messageId: string): Promise<void> {
        try {
            const messageRef = doc(db, `directMessages/${conversationId}/messages`, messageId);
            await deleteDoc(messageRef);
        } catch (error) {
            console.error('Error deleting message:', error);
            throw error;
        }
    }

        async sendAttachment(conversationId: string, file: File, senderUid: string): Promise<void> {
        try {
            const messageId = this.generateMessageId();

            // Create a unique file path in Storage
            const timestamp = Date.now();
            const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
            const filePath = `directMessages/${conversationId}/${timestamp}_${sanitizedFileName}`;
            const storageRef = ref(storage, filePath);

            // Upload file to Firebase Storage
            await uploadBytes(storageRef, file);

            // Get download URL
            const downloadUrl = await getDownloadURL(storageRef);

            // Create attachment message
            const attachmentMessage: Message = {
                id: messageId,
                message: file.name,
                type: 'attachment',
                senderUid,
                timestamp: serverTimestamp(),
                readBy: [senderUid],
                attachments: [{
                    name: file.name,
                    url: downloadUrl,
                    type: file.type,
                    size: file.size,
                }],
            };

            const messageRef = doc(db, `directMessages/${conversationId}/messages`, messageId);
            const conversationRef = doc(db, 'directMessages', conversationId);

            const batch = writeBatch(db);

            batch.set(messageRef, attachmentMessage);
            batch.update(conversationRef, {
                updatedAt: serverTimestamp(),
            });

            await batch.commit();
        } catch (error) {
            console.error('Error sending attachment:', error);
            throw error;
        }
    }
}

export const directMessagingService = new DirectMessagingService();